import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'

// ============================================================
// usePhotos - Manages promo photo uploads and storage
// Photos are stored in TWO places:
//   1. Supabase Storage - the actual image file (binary data)
//   2. Supabase Database - the metadata (name, tags, category)
// This hook handles both and combines them
// ============================================================

export function usePhotos() {

  const [photos, setPhotos] = useState([]) // [] = empty list to start
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // -------------------------------------------------------
  // Auto-fetch photos when hook is first used
  // -------------------------------------------------------
  useEffect(() => {
    fetchPhotos()
  }, [])


  // -------------------------------------------------------
  // fetchPhotos - Loads photo metadata from database
  // Then attaches the public URL for each photo
  // -------------------------------------------------------
  const fetchPhotos = async () => {

    try {
      setLoading(true)
      setError(null)

      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) throw userError
      if (!user) throw new Error('Not authenticated')

      const { data, error: fetchError } = await supabase
        .from('photos')          // go to the photos TABLE (metadata only)
        .select('*')             // get all columns: id, name, file_path, tags, etc
        .eq('user_id', user.id)  // only this users photos
        .order('uploaded_at', { ascending: false })
        // newest photos first
        // ascending: false = reverse chronological order

      if (fetchError) throw fetchError

      // Now get the public URL for each photo
      // The database only stores the file PATH, not the full URL
      // We need to ask Supabase Storage for the actual web URL
      const photosWithUrls = await Promise.all(
        // Promise.all = run all these async operations at the same time
        // much faster than doing them one by one in a loop
        // waits for ALL of them to finish before moving on

        (data || []).map(async (photo) => {
          // .map() goes through each photo metadata row
          // async on the arrow function because getPublicUrl needs await

          const { data: urlData } = supabase.storage
            // .storage = Supabase file storage (not the database)
            .from('photos')           // 'photos' storage bucket name
            .getPublicUrl(photo.file_path)
            // photo.file_path = something like "userid/1709999_abc.jpg"
            // returns the full web URL for this file

          return {
            ...photo,           // ... spread = copy all database fields
            url: urlData.publicUrl
            // add the URL on top of the database fields
            // result: { id, name, file_path, tags, category, url }
          }
        })
      )

      setPhotos(photosWithUrls)
      // save the combined list to state

    } catch (err) {
      setError(err.message)
      console.error('Error fetching photos:', err)
    } finally {
      setLoading(false) // always turn off spinner
    }
  }


  // -------------------------------------------------------
  // uploadPhoto - Uploads a photo file + saves its info
  // Two-step process: storage first, then database
  // -------------------------------------------------------
  const uploadPhoto = async (file, metadata) => {
    // file = the actual File object from the browser file picker
    // metadata = { name: "Red Gown", category: "headshots", tags: ["red", "gown"] }

    try {
      setError(null)

      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) throw userError
      if (!user) throw new Error('Not authenticated')

      // ---- BUILD A UNIQUE FILE NAME ----
      const fileExt = file.name.split('.').pop()
      // file.name = "my_headshot.jpg"
      // .split('.') = ["my_headshot", "jpg"]
      // .pop() = removes and returns last item = "jpg"
      // we need the extension to keep the file type correct

      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`
      // ` ` = template literal - text with embedded variables using ${}
      // Date.now() = milliseconds since 1970 - unique timestamp like 1709999999999
      // Math.random() = random decimal like 0.123456789
      // .toString(36) = convert to base-36 (uses letters and numbers)
      // .substring(7) = take 7 characters from it
      // combined result example: "1709999999999_k3b7m2.jpg"
      // virtually impossible for two files to get the same name

      const filePath = `${user.id}/${fileName}`
      // store inside a folder named after the user's ID
      // example: "abc-123-user-id/1709999999999_k3b7m2.jpg"
      // keeps different users' files completely separate

      // ---- STEP 1: UPLOAD THE ACTUAL FILE TO STORAGE ----
      const { error: uploadError } = await supabase.storage
        // .storage = file storage, not the database
        .from('photos')         // 'photos' storage bucket
        .upload(filePath, file, {
          // filePath = where to save it
          // file = the actual image data
          cacheControl: '3600', // browsers can cache this file for 1 hour (3600 seconds)
          upsert: false         // false = NEVER overwrite an existing file
          // if somehow the same filename exists, throw error instead
        })

      if (uploadError) throw uploadError
      // if file upload failed, stop here
      // dont save to database if the file isnt actually stored

      // ---- STEP 2: SAVE THE METADATA TO THE DATABASE ----
      const { data: metaData, error: metaError } = await supabase
        // data: metaData = rename to avoid clashing with other 'data' variables
        .from('photos')   // NOW go to the photos DATABASE table
        .insert([{
          user_id: user.id,
          name: metadata.name,                       // display name they chose
          file_path: filePath,                       // path in storage
          category: metadata.category || 'headshots',// use their choice or default
          tags: metadata.tags || [],                 // array of tags or empty array
          uploaded_at: new Date().toISOString()      // current time in ISO format
        }])
        .select()

      if (metaError) throw metaError

      if (!metaData || metaData.length === 0)
        throw new Error('Failed to save photo metadata')
      // safety check - make sure the database insert actually worked

      // ---- GET THE PUBLIC URL TO DISPLAY THE IMAGE ----
      const { data: urlData } = supabase.storage
        .from('photos')
        .getPublicUrl(filePath)
        // get the full web URL like "https://xxx.supabase.co/storage/..."

      const newPhoto = {
        ...metaData[0],          // spread all database fields
        url: urlData.publicUrl   // attach the display URL
      }

      setPhotos([newPhoto, ...photos])
      // add new photo to FRONT of list (newest first)
      // [newPhoto, ...photos] = new item, then rest of existing photos

      return newPhoto

    } catch (err) {
      setError(err.message)
      console.error('Error uploading photo:', err)
      throw err
    }
  }


  // -------------------------------------------------------
  // deletePhoto - Removes photo from storage AND database
  // Order matters: storage first, then database
  // -------------------------------------------------------
  const deletePhoto = async (photoId, filePath) => {
    // photoId = the database row ID
    // filePath = the storage path like "userid/filename.jpg"
    // need BOTH because they live in two separate places

    try {
      setError(null)

      // ---- STEP 1: DELETE FROM STORAGE FIRST ----
      const { error: storageError } = await supabase.storage
        .from('photos')
        .remove([filePath])
        // .remove() deletes files from storage
        // [filePath] = list because you CAN delete multiple files at once
        // even deleting one still needs to be in a list

      if (storageError) throw storageError
      // IMPORTANT: if storage deletion fails, stop HERE
      // do NOT delete the database record
      // reason: if file still exists but record is gone = orphaned file
      // you would have a file taking up space with no way to find or delete it

      // ---- STEP 2: DELETE FROM DATABASE SECOND ----
      const { error: dbError } = await supabase
        .from('photos')
        .delete()
        .eq('id', photoId)
        // WHERE id = this specific photo

      if (dbError) throw dbError

      setPhotos(photos.filter(p => p.id !== photoId))
      // .filter() keeps everything EXCEPT the deleted photo
      // p => = each photo called 'p'
      // p.id !== photoId = keep if id does NOT match deleted one

    } catch (err) {
      setError(err.message)
      console.error('Error deleting photo:', err)
      throw err
    }
  }


  // -------------------------------------------------------
  // updatePhoto - Updates photo metadata (name, tags, etc)
  // Does NOT change the actual image file
  // -------------------------------------------------------
  const updatePhoto = async (photoId, updates) => {
    // photoId = which photo to update
    // updates = object with changed fields like { name: "New Name", tags: ["red"] }

    try {
      setError(null)

      const { data, error: updateError } = await supabase
        .from('photos')
        .update(updates)        // only update the fields passed in
        .eq('id', photoId)      // find this specific photo
        .select()               // return the updated row

      if (updateError) throw updateError

      // Re-fetch the public URL using the updated data
      // even though the file didnt change, we need URL for the state object
      const { data: urlData } = supabase.storage
        .from('photos')
        .getPublicUrl(data[0].file_path)
        // data[0] = the updated row from database
        // .file_path = the storage path that didnt change

      const updatedPhoto = {
        ...data[0],              // all updated database fields
        url: urlData.publicUrl   // the display URL
      }

      setPhotos(photos.map(p => p.id === photoId ? updatedPhoto : p))
      // .map() replaces the old photo with the updated version
      // p.id === photoId ? updatedPhoto : p = ternary
      //   if this photo matches = use new version
      //   if not = keep original

      return updatedPhoto

    } catch (err) {
      setError(err.message)
      console.error('Error updating photo:', err)
      throw err
    }
  }


  // Return everything the PhotoLibrary page needs
  return {
    photos,       // array of photo objects (each has url attached)
    loading,      // boolean - true while fetching
    error,        // error message or null
    uploadPhoto,  // function(file, metadata) - uploads new photo
    deletePhoto,  // function(photoId, filePath) - removes photo
    updatePhoto,  // function(photoId, updates) - edits metadata
    fetchPhotos   // function() - manually reload
  }
}